import React, { useCallback, useEffect, useRef, useState } from 'react';

import BudgetStructureBulkUpload from '../../department/BudgetStructureBulkUpload';

import StructureSection from './depts/StructureSection';
import StepShell from './StepShell';

import { escapeHtml } from 'Helpers/escapeHtml';

import { useToast } from 'Hooks/useToast';

const EXPENSE = 'Expense';
const REVENUE = 'Revenue';

/** One line per destination, naming each risk and opportunity a delete moved there. */
const describeMoved = (moved) => {
    const groups = new Map();
    moved.forEach((move) => {
        if (!groups.has(move.destination)) groups.set(move.destination, []);
        groups.get(move.destination).push(move);
    });

    return [...groups.values()].map((group) => {
        const items = group
            .map((move) =>
                move.risk === 0 ? $t('{{name}} (opportunity)', { name: escapeHtml(move.name) }) : $t('{{name}} (risk)', { name: escapeHtml(move.name) }),
            )
            .join(', ');

        if (group[0].central) {
            return group[0].revenue
                ? $t('Moved to Central (no longer counted in AFR): {{items}}', { items })
                : $t('Moved to Central (no longer counted in AFC): {{items}}', { items });
        }

        return $t('Moved to {{parent}}: {{items}}', { parent: escapeHtml(group[0].label), items });
    });
};

const DASHED_BUTTON =
    'tw-rounded-lg tw-border tw-border-dashed tw-border-charcoal-25 hover:tw-border-blue tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-charcoal';

/**
 * Departments & cost centres. Every read and write returns the whole structure
 * plus the wizard payload, so the page always re-renders from the server's
 * copy — renaming re-slugs a department, and its "Edit details" link with it.
 */
export default function DeptsStep({ step, urls, deferring, saving, onDefer, onNext, onStatusChange }) {
    const [data, setData] = useState(null);
    const [adding, setAdding] = useState(null);
    const [importing, setImporting] = useState(false);
    const toast = useToast();

    // Responses can land out of order (a refetch racing a write), so a payload
    // is applied only when it is newer than the last one applied. A later
    // request failing must not discard an earlier one's success.
    const nextRequest = useRef(0);
    const lastApplied = useRef(0);

    const send = useCallback(
        (request, isRead = false) => {
            const id = ++nextRequest.current;

            return request().then(({ data }) => {
                if (id > lastApplied.current) {
                    lastApplied.current = id;
                    setData(data);
                    onStatusChange(data.wizard);
                } else if (!isRead) {
                    // A write answered after a newer response was applied: that
                    // snapshot may predate it, so read the structure again.
                    send(() => axios.get(urls.data), true).catch(() => {});
                }
            });
        },
        [onStatusChange, urls.data],
    );

    const load = useCallback(
        () => send(() => axios.get(urls.data), true).catch(() => toast.error($t('Something went wrong. Please try again.'))),
        [send, urls.data],
    );

    useEffect(() => {
        load();
    }, [urls.data]);

    const itemUrl = (item) => urls.item_template.replace('__TYPE__', item.item_type).replace('__ID__', item.id);

    // A validation failure is rethrown for the inline form to show against its
    // field. Anything else means the page is out of date, so it is reloaded.
    const handleWriteError = (e) => {
        if (e?.response?.status !== 422) {
            toast.error(e?.response?.data?.message || $t('Something went wrong. Please try again.'));
            load();
        }

        throw e;
    };

    const store = (body) => send(() => axios.post(urls.items, body)).catch(handleWriteError);

    const addDepartment = (type) => (name) => store({ item_type: 'department', type, name }).then(() => setAdding(null));

    const addCentre = (type) => (department, name) => store({ item_type: 'costcenter', type, name, department_id: department.id });

    const rename = (item, name) => send(() => axios.put(itemUrl(item), { name })).catch(handleWriteError);

    // A delete reports where the item's risks and opportunities moved.
    const remove = (item) =>
        send(() =>
            axios.delete(itemUrl(item)).then((response) => {
                describeMoved(response.data?.moved || []).forEach((line) => toast.info(line, 15000));

                return response;
            }),
        )
            .catch(handleWriteError)
            .catch(() => {});

    const lede = $t(
        'Add expenditure and revenue departments, reflecting your own work breakdown structure. Once added you can edit them to include longer descriptions of scope, assumptions and exclusions. Add cost and revenue centres to departments.',
    );

    if (!data) {
        return (
            <StepShell step={step} lede={lede} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    return (
        <StepShell step={step} lede={lede} deferring={deferring} saving={saving} onDefer={onDefer} onNext={onNext}>
            <div className="tw-flex tw-flex-col tw-gap-y-2 tw-max-w-2xl">
                <StructureSection
                    id="setup-wizard-expenditure"
                    typeLabel={$t('Expenditure')}
                    emptyText={$t('No expenditure departments yet')}
                    addCentreLabel={$t('+ Add cost centre')}
                    departments={data.expenditure}
                    canEdit={data.can_edit_expenditure}
                    canRename={data.can_rename_expenditure}
                    canDelete={data.can_delete_expenditure}
                    canOpenDetails={data.can_open_details}
                    adding={adding === EXPENSE}
                    onAddDepartment={addDepartment(EXPENSE)}
                    onCancelAdd={() => setAdding(null)}
                    onAddCentre={addCentre(EXPENSE)}
                    onRename={rename}
                    onDelete={remove}
                />

                <StructureSection
                    id="setup-wizard-revenue"
                    typeLabel={$t('Revenue')}
                    emptyText={$t('No revenue departments yet')}
                    addCentreLabel={$t('+ Add revenue centre')}
                    departments={data.revenue}
                    canEdit={data.can_edit_revenue}
                    canRename={data.can_rename_revenue}
                    canDelete={data.can_delete_revenue}
                    canOpenDetails={data.can_open_details}
                    adding={adding === REVENUE}
                    onAddDepartment={addDepartment(REVENUE)}
                    onCancelAdd={() => setAdding(null)}
                    onAddCentre={addCentre(REVENUE)}
                    onRename={rename}
                    onDelete={remove}
                />

                {(data.can_edit_expenditure || data.can_edit_revenue) && (
                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-1">
                        {data.can_edit_expenditure && (
                            <button type="button" className={DASHED_BUTTON} onClick={() => setAdding(EXPENSE)}>
                                {$t('+ Add expenditure department')}
                            </button>
                        )}
                        {data.can_edit_revenue && (
                            <button type="button" className={DASHED_BUTTON} onClick={() => setAdding(REVENUE)}>
                                {$t('+ Add revenue department')}
                            </button>
                        )}
                    </div>
                )}

                {data.can_import && !importing && (
                    <div>
                        <button type="button" className={DASHED_BUTTON} onClick={() => setImporting(true)}>
                            {$t('Import structure / start from template')}
                        </button>
                    </div>
                )}

                {data.can_import && importing && (
                    <div className="tw-p-4 tw-rounded-lg tw-border tw-border-charcoal-15">
                        <BudgetStructureBulkUpload
                            bulkPreviewUrl={data.import.bulk_preview_url}
                            bulkConfirmUrl={data.import.bulk_confirm_url}
                            templateUrl={data.import.template_url}
                            onSuccess={() => {
                                setImporting(false);
                                load();
                            }}
                            onCancel={() => setImporting(false)}
                        />
                    </div>
                )}
            </div>
        </StepShell>
    );
}
